// src/utils/consent.ts
//
// Whether we are allowed to store or read anything on the visitor's device
// beyond what the site needs to function.
//
// This used to be decided AFTER the fact. Google Analytics was a plain <script>
// in index.html's <head>, so it loaded, fired a pageview and set its cookies
// before React mounted - and the banner then appeared a second later to ask
// permission for something that had already happened. Declining stored a flag
// that nothing ever read again.
//
// UK PECR governs storing or reading information on someone's device, and
// non-essential cookies - analytics and advertising - need consent BEFORE they
// are set. Consent collected afterwards is not consent.
//
// So nothing loads from here until there is an explicit accept, and the stored
// decision is re-applied on every load rather than only at the moment it is made.

export type ConsentDecision = 'accepted' | 'declined';

const CONSENT_KEY = 'strymhub_cookie_consent';

/** Fired when the decision changes, so the banner can reappear on withdrawal. */
export const CONSENT_CHANGED_EVENT = 'strymhub:consent-changed';

/**
 * The stored decision, or null if none has been made.
 *
 * Reads are wrapped because localStorage throws outright in some browsers
 * configured to block site data - and a visitor with storage disabled must still
 * get a working site, tracked or not.
 */
export function getConsent(): ConsentDecision | null {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === 'accepted' || value === 'declined' ? value : null;
  } catch {
    // Treated as "no decision", which means nothing non-essential loads. The
    // safe direction: a visitor we cannot ask is not a visitor we may track.
    return null;
  }
}

/** Records a decision and applies it immediately. */
export function setConsent(decision: ConsentDecision): void {
  try {
    localStorage.setItem(CONSENT_KEY, decision);
  } catch {
    // Storage unavailable. The decision still applies to this page view; it
    // simply will not be remembered, and the visitor will be asked again.
  }

  applyConsent();
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT));
}

/**
 * Withdrawal. Required to be as easy as giving consent, and until now there was
 * no way at all: once a decision was stored the banner never returned.
 *
 * Deliberately does NOT reload the page to unload analytics. It cannot be
 * unloaded once running, so the honest thing is to stop it collecting anything
 * further and to not load it on the next visit, rather than to imply the
 * previous session can be undone.
 */
export function withdrawConsent(): void {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* nothing to remove if storage is unavailable */
  }

  denyTracking();
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT));
}

/**
 * Applies whatever has been decided. Called once at start-up and again whenever
 * the decision changes.
 *
 * The absence of a decision is NOT neutral - it means deny. That is the whole
 * correction: previously "no decision yet" and "accepted" behaved identically.
 */
export function applyConsent(): void {
  if (getConsent() === 'accepted') {
    loadAnalytics();
    return;
  }

  denyTracking();
}

/**
 * Nothing to switch off any more.
 *
 * This used to ask AdSense for non-personalised ads. Google advertising has been
 * removed entirely, so there is no ad network to instruct - analytics is now the
 * only non-essential thing the site can load, and not loading it IS the denial.
 *
 * Kept as a named step rather than an empty branch so the decision reads
 * explicitly at the call site, and so re-introducing an ad provider has an obvious
 * place to go.
 */
function denyTracking(): void {
  /* nothing non-essential is loaded unless consent is given */
}

let analyticsLoaded = false;

/**
 * Injects the GA script. Only reachable from an explicit accept.
 *
 * Guarded so a second accept, or a re-applied decision on navigation, cannot
 * load it twice and double-count every pageview.
 */
function loadAnalytics(): void {
  if (analyticsLoaded) return;

  const id = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;
  if (!id || id === 'G-XXXXXXXXXX') return;

  analyticsLoaded = true;

  const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  if (!w.gtag) {
    w.gtag = function gtag(...args: unknown[]) {
      w.dataLayer!.push(args);
    };
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  w.gtag('js', new Date());
  w.gtag('config', id);
}

/** Exposed for tests: forget that analytics was loaded in this page's lifetime. */
export function __resetForTests(): void {
  analyticsLoaded = false;
}
